class RecentlyViewed extends HTMLElement {
  constructor() {
    super();
    this.storageKey = 'recently_viewed_products';
    this.horizonKey = '_rviewed';
    this.maxItems = 10;
  }

  connectedCallback() {
    const currentProductId = this.dataset.productId;
    const currentProductHandle = this.dataset.productHandle;
    const maxProducts = parseInt(this.dataset.maxProducts) || 4;

    if (currentProductHandle) {
      this.recordProduct(currentProductId, currentProductHandle);
    }

    const viewed = this.getMergedRecentlyViewed();

    const toDisplay = viewed
      .filter(item => item.handle !== currentProductHandle)
      .slice(0, maxProducts);

    if (toDisplay.length === 0) {
      this.style.display = 'none';
      return;
    }

    this.fetchAndRender(toDisplay);
  }

  recordProduct(id, handle) {
    let viewed = this.getOwnRecentlyViewed();
    viewed = viewed.filter(item => item.id !== id);
    viewed.unshift({ id, handle });
    viewed = viewed.slice(0, this.maxItems);
    localStorage.setItem(this.storageKey, JSON.stringify(viewed));
  }

  getOwnRecentlyViewed() {
    try {
      return JSON.parse(localStorage.getItem(this.storageKey) || '[]');
    } catch {
      return [];
    }
  }

  getHorizonRecentlyViewed() {
    try {
      const raw = localStorage.getItem(this.horizonKey);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.map(handle => ({ id: null, handle }));
    } catch {
      return [];
    }
  }

  getMergedRecentlyViewed() {
    const own = this.getOwnRecentlyViewed();
    const horizon = this.getHorizonRecentlyViewed();
    const seen = new Set(own.map(i => i.handle));
    const merged = [...own];
    for (const item of horizon) {
      if (!seen.has(item.handle)) {
        seen.add(item.handle);
        merged.push(item);
      }
    }
    return merged.slice(0, this.maxItems);
  }

  async fetchAndRender(products) {
    try {
      const productDataArray = await Promise.all(
        products.map(item =>
          fetch(`/products/${item.handle}.js`)
            .then(r => r.json())
            .catch(() => null)
        )
      );

      const validProducts = productDataArray.filter(Boolean);

      if (validProducts.length === 0) {
        this.style.display = 'none';
        return;
      }

      this.renderFromJson(validProducts);

    } catch (err) {
      console.error('Recently Viewed: Failed to fetch products', err);
      this.style.display = 'none';
    }
  }

  injectMobileStyles() {
    const styleId = 'recently-viewed-mobile-fix';
    if (document.getElementById(styleId)) return;
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @media (max-width: 749px) {
        recently-viewed .resource-list__item {
          flex: 0 0 calc((100% - 12px) / 2) !important;
          max-width: calc((100% - 12px) / 2) !important;
        }
        recently-viewed .recently-viewed__list {
          flex-wrap: wrap !important;
        }
      }
    `;
    document.head.appendChild(style);
  }

  renderFromJson(products) {
    const layoutType = this.dataset.layoutType;
    const carouselOnMobile = this.dataset.carouselOnMobile === 'true';
    const mobileColumns = parseInt(this.dataset.mobileColumns) || 2;
    const listEl = this.querySelector('.recently-viewed__list');
    const mobileListEl = this.querySelector('.recently-viewed__list--mobile');
    const maxProducts = parseInt(this.dataset.maxProducts) || 4;

    if (!listEl) return;

    // On mobile, only show the 2 most recent products
    const isMobile = window.innerWidth <= 749;
    const displayProducts = isMobile ? products.slice(0, 2) : products;

    const cards = displayProducts.map(product => {
      const price = this.formatMoney(product.price);
      const comparePrice = product.compare_at_price && product.compare_at_price > product.price
        ? this.formatMoney(product.compare_at_price)
        : null;

      const imageUrl = product.featured_image
        ? product.featured_image.replace(/\?.*$/, '') + '?width=600'
        : '';

      return `
        <div class="resource-list__item" style="text-align:center;">
          <a href="/products/${product.handle}" style="display:block;text-decoration:none;color:inherit;">
            ${imageUrl ? `
              <div style="
                aspect-ratio:1/1;
                overflow:hidden;
                border-radius:25px;
                margin-bottom:8px;
              ">
                <img
                  src="${imageUrl}"
                  alt="${this.escapeHtml(product.title)}"
                  loading="lazy"
                  style="width:100%;height:100%;object-fit:cover;display:block;"
                >
              </div>
            ` : ''}
            <h6 style="
              margin:0 0 4px;
              font-size:1rem;
              font-weight:700;
              color:var(--color-foreground);
              text-align:center;
            ">
              ${comparePrice ? `<s style="opacity:0.6;margin-right:4px;font-weight:normal;">${comparePrice}</s>` : ''}
              <span>${price}</span>
            </h6>
            <p style="
              margin:0;
              font-size:0.8rem;
              color:var(--color-foreground);
              opacity:0.8;
              text-align:center;
            ">
              ${this.escapeHtml(product.title)}
            </p>
          </a>
        </div>
      `;
    });

    const columnCount = parseInt(this.dataset.maxProducts) || 4;
    const needsCentering = products.length < columnCount;

    listEl.innerHTML = cards.join('');
    listEl.dataset.hasRecommendations = 'true';

    if (layoutType === 'carousel') {
      listEl.style.display = 'flex';
      listEl.style.flexWrap = 'nowrap';
      listEl.style.overflowX = 'auto';
      listEl.style.gap = 'var(--resource-list-column-gap-desktop, 12px)';
      listEl.style.scrollSnapType = 'x mandatory';
      listEl.style.maxWidth = 'var(--page-width, 1200px)';
      listEl.style.marginLeft = 'auto';
      listEl.style.marginRight = 'auto';
      listEl.style.paddingLeft = 'var(--page-margin, 20px)';
      listEl.style.paddingRight = 'var(--page-margin, 20px)';
      listEl.style.boxSizing = 'border-box';

      listEl.querySelectorAll('.resource-list__item').forEach(item => {
        item.style.flex = '0 0 calc((100% - (var(--column-count, 4) - 1) * var(--resource-list-column-gap-desktop, 12px)) / var(--column-count, 4))';
        item.style.scrollSnapAlign = 'start';
        item.style.minWidth = '0';
      });

    } else {
      listEl.style.maxWidth = 'var(--page-width, 1200px)';
      listEl.style.marginLeft = 'auto';
      listEl.style.marginRight = 'auto';
      listEl.style.paddingLeft = 'var(--page-margin, 20px)';
      listEl.style.paddingRight = 'var(--page-margin, 20px)';
      listEl.style.boxSizing = 'border-box';

      if (needsCentering) {
        listEl.style.display = 'flex';
        listEl.style.flexWrap = 'wrap';
        listEl.style.justifyContent = 'center';
        listEl.style.gap = 'var(--resource-list-column-gap-desktop, 12px)';

        const itemWidth = `calc((100% - (var(--column-count, 4) - 1) * var(--resource-list-column-gap-desktop, 12px)) / var(--column-count, 4))`;
        listEl.querySelectorAll('.resource-list__item').forEach(item => {
          item.style.flex = `0 0 ${itemWidth}`;
          item.style.maxWidth = itemWidth;
        });
      }

      if (carouselOnMobile && mobileListEl) {
        mobileListEl.innerHTML = cards.join('');
        mobileListEl.dataset.hasRecommendations = 'true';

        mobileListEl.style.display = 'grid';
        mobileListEl.style.gridTemplateColumns = `repeat(${mobileColumns}, 1fr)`;
        mobileListEl.style.gap = 'var(--resource-list-gap, 12px)';
        mobileListEl.style.paddingLeft = 'var(--page-margin, 20px)';
        mobileListEl.style.paddingRight = 'var(--page-margin, 20px)';
        mobileListEl.style.boxSizing = 'border-box';
      }
    }

    // Always inject mobile 2-column fix
    this.injectMobileStyles();
  }

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return '$' + (amount.endsWith('.00') ? amount.slice(0, -3) : amount);
  }

  escapeHtml(str) {
    const div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }
}

customElements.define('recently-viewed', RecentlyViewed);